import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface DispatchForm {
  jobId: string;
  jobStatus: string;
  dispatchOrderDate: string;
  totalBoxDispatch: string;
}

interface Seed {
  Job_ID: string; PO_RefNumber: string | null; Job_Name: string | null;
  Total_Boxes: string | null; Sheet_Size: string | null;
  PO_Number: string | null; PO_Artwork: string | null;
  PO_priceperbox: string | null; PO_Amount: string | null;
  PO_Vat: string | null; PO_FinalAmount: string | null;
  Sheet_GSM: string | null;
  TopFinish_Name: string | null;
  Client_Name: string | null; Client_Address_Billing: string | null; Client_Address_Dispatch: string | null;
}

interface JobOpt { id: string; label: string }

const today = () => new Date().toISOString().slice(0, 10);

const empty: DispatchForm = {
  jobId: '', jobStatus: 'Dispatched',
  dispatchOrderDate: today(), totalBoxDispatch: '0',
};

export function DispatchFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<DispatchForm>(empty);
  const [seed, setSeed] = useState<Seed | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recentJobs = useQuery({
    queryKey: ['dispatch-jobs'],
    queryFn: () => api<JobOpt[]>('/api/dispatches/jobs/recent?limit=500'),
    staleTime: 60_000,
  });

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['dispatch', id],
    queryFn: () => api<DispatchForm & { id: string }>(`/api/dispatches/${id}`),
  });

  useEffect(() => {
    if (existing.data) {
      setForm({ ...empty, ...existing.data });
      if (existing.data.jobId) loadSeed(existing.data.jobId);
    }
  }, [existing.data]);

  async function loadSeed(jobId: string) {
    if (!jobId) { setSeed(null); return; }
    try { setSeed(await api<Seed>(`/api/dispatches/seed/${encodeURIComponent(jobId)}`)); }
    catch { setSeed(null); }
  }

  function selectJob(jobId: string) {
    setForm((f) => ({ ...f, jobId }));
    loadSeed(jobId);
    // pre-fill dispatched boxes from job's total boxes (operator can adjust)
    api<Seed>(`/api/dispatches/seed/${encodeURIComponent(jobId)}`).then((s) => {
      if (s.Total_Boxes) setForm((f) => ({ ...f, totalBoxDispatch: s.Total_Boxes! }));
    }).catch(() => {});
  }

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/dispatches/${id}` : '/api/dispatches', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dispatches'] }); navigate('/dispatches'); },
    onError: (e) => {
      const msg = (e as Error).message;
      const qc = /^exceeds_qc_approved:(-?\d+)$/.exec(msg);
      const rem = /^exceeds_remaining:(-?\d+)$/.exec(msg);
      setError(qc
        ? `Only ${Math.max(0, Number(qc[1])).toLocaleString()} cartons are approved by Quality and not yet dispatched — dispatch at most that.`
        : rem ? `Cannot exceed the remaining quantity (${Math.max(0, Number(rem[1])).toLocaleString()} available).` : msg);
    },
  });

  function update<K extends keyof DispatchForm>(k: K, v: string) { setForm((f) => ({ ...f, [k]: v })); }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.jobId) { setError('Job is required.'); return; }
    save.mutate();
  }

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {isEdit ? `Edit Dispatch — ${id}` : 'New Dispatch'}
        </h1>
        <Link to={listReturnUrl('/dispatches')} className="text-sm text-slate-600 hover:underline">← Back</Link>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <Section title="Order">
          <div>
            <label className="label">Dispatch Order Date</label>
            <input type="date" className="input" value={form.dispatchOrderDate}
              onChange={(e) => update('dispatchOrderDate', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="label">Select Job * (top 500 recent)</label>
            <select className="input" value={form.jobId} onChange={(e) => selectJob(e.target.value)}>
              <option value="">— select a job —</option>
              {recentJobs.data?.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
              {form.jobId && !recentJobs.data?.find((j) => j.id === form.jobId) && (
                <option value={form.jobId}>{form.jobId} (existing)</option>
              )}
            </select>
          </div>
        </Section>

        {seed && (
          <Section title="Job & PO snapshot (read-only)">
            <Read label="PO Number" value={seed.PO_Number ?? ''} />
            <Read label="PO Artwork" value={seed.PO_Artwork ?? ''} />
            <Read label="Client" value={seed.Client_Name ?? ''} />
            <Read label="Billing Address" value={seed.Client_Address_Billing ?? ''} />
            <Read label="Dispatch Address" value={seed.Client_Address_Dispatch ?? ''} />
            <Read label="GSM" value={seed.Sheet_GSM ?? ''} />
            <Read label="Lamination Type" value={seed.TopFinish_Name ?? ''} />
            <Read label="Price / Box" value={seed.PO_priceperbox ?? ''} />
            <Read label="Gross Amount" value={seed.PO_Amount ?? ''} />
            <Read label="GST %" value={seed.PO_Vat ?? ''} />
            <Read label="Final Amount" value={seed.PO_FinalAmount ?? ''} />
            <Read label="Total Boxes (job)" value={seed.Total_Boxes ?? ''} />
          </Section>
        )}

        <Section title="Dispatch">
          <div>
            <label className="label">Total Boxes Dispatched</label>
            <input className="input" type="number" value={form.totalBoxDispatch}
              onChange={(e) => update('totalBoxDispatch', e.target.value)}
              step="any" />
          </div>
          <div>
            <label className="label">Job Status</label>
            <select className="input" value={form.jobStatus} onChange={(e) => update('jobStatus', e.target.value)}>
              <option>Dispatched</option>
              <option>Partially Dispatched</option>
              <option>In Progress</option>
              <option>Done</option>
              <option>Pending</option>
              <option>Cancelled</option>
            </select>
          </div>
        </Section>

        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Link to={listReturnUrl('/dispatches')} className="btn-ghost">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">{title}</div>
      <div className="card-body grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}
function Read({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input bg-slate-50" value={value} readOnly />
    </div>
  );
}
